import { match } from "ts-pattern";

const locales = [
	"nl-nl",
	"en-nl",
	"de-at",
	"en-at",
	"fr-fr",
	"en-fr",
	"de-de",
	"en-de",
	"it-it",
	"en-it",
	"es-es",
	"en-es",
	"en-ie",
	"nl-be",
	"fr-be",
	"en-be",
	"de-lu",
	"fr-lu",
	"en-lu",
	"en-pt",
	"en-fi",
	"en-cz",
	"en-dk",
	"en-se",
	"en-pl",
] as const;

type LocaleType = (typeof locales)[number];
type LanguageType = LocaleType extends `${infer L}-${infer _}` ? L : never;
type CountryType = LocaleType extends `${infer _}-${infer C}` ? C : never;

export function separateLocale(locale: string) {
	const [language, country] = locale.toLowerCase().replace("-", "_").split("_");

	return {
		language: (language ?? "nl") as LanguageType,
		country: (country ?? "nl") as CountryType,
	};
}

type FormatType = "language-country" | "language-COUNTRY" | "language_country" | "language_COUNTRY";

type TransformLocale<
	L extends string,
	S extends "-" | "_",
	U extends boolean,
> = L extends `${infer Lang}-${infer Country}`
	? `${Lang}${S}${U extends true ? Uppercase<Country> : Country}`
	: never;

type LocaleFormats = {
	"language-country": LocaleType;
	"language-COUNTRY": TransformLocale<LocaleType, "-", true>;
	language_country: TransformLocale<LocaleType, "_", false>;
	language_COUNTRY: TransformLocale<LocaleType, "_", true>;
};

export function formatLocale<F extends FormatType>(
	locale: string,
	format: F = "language-country" as F,
) {
	const { language, country } = separateLocale(locale);

	const formatted = match(format as FormatType)
		.with("language-country", () => `${language}-${country}`)
		.with("language-COUNTRY", () => `${language}-${country.toUpperCase()}`)
		.with("language_country", () => `${language}_${country}`)
		.with("language_COUNTRY", () => `${language}_${country.toUpperCase()}`)
		.exhaustive();

	return formatted as LocaleFormats[F];
}

type PatternSupportedType =
	| { pattern: "language-country"; supported: LocaleType[] }
	| { pattern: "language"; supported: LanguageType[] }
	| { pattern: "country"; supported: CountryType[] };

type IsLocaleInput = { current: string } & PatternSupportedType;

export function isLocale({ current, pattern, supported }: IsLocaleInput): boolean {
	const { language, country } = separateLocale(current);

	return match(pattern)
		.with("language", () => supported.includes(language))
		.with("country", () => supported.includes(country))
		.with("language-country", () => supported.includes(current))
		.exhaustive();
}

export function getDomainDisplayNames(locale: string) {
	const { language, country } = separateLocale(locale);

	const regionDisplay = new Intl.DisplayNames([language], { type: "region" });
	const languageDisplay = new Intl.DisplayNames([language], { type: "language" });

	return {
		region: regionDisplay.of(country.toUpperCase()),
		language: languageDisplay.of(language),
	};
}
